/*
 * RatingPanel — Generic 1-5 yıldız + comment + submit UI.
 *
 * İki kullanım modu:
 *   • inline: WAITER tab'ında resolved call sonrası ofisanta qiymət için.
 *   • modal:  "Restorana qiymət ver" akışı için. <RatingModal /> ile sarmala.
 *
 * Submit handler async olabilir; busy true verirsek butonlar disable.
 * Tasarım: native iOS feel — yıldızlar üzerinde tap'te scale, karekana.
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Star, Send, X, Loader2, ShieldCheck } from 'lucide-react';

const haptic = (ms = 8) => {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      (navigator as any).vibrate(ms);
    }
  } catch {
    /* noop */
  }
};

export interface RatingPanelProps {
  title: string;
  subtitle?: string;
  /** 1-5 arası seçili yıldız ile çağrılır. */
  onSubmit: (rating: number, comment: string) => void | Promise<void>;
  onCancel?: () => void;
  initialRating?: number;
  initialComment?: string;
  busy?: boolean;
  /** İcon arka planı için renk şeması. */
  tone?: 'orange' | 'indigo' | 'emerald';
  /** Submit etiketi — varsayılan "Göndər". */
  submitLabel?: string;
  /** Yıldız altı placeholder. */
  commentPlaceholder?: string;
  /** Cancel butonu göster. */
  showCancel?: boolean;
  cancelLabel?: string;
}

const TONE_STYLES = {
  orange: {
    iconBg: 'bg-orange-100 dark:bg-orange-500/20',
    iconColor: 'text-orange-600 dark:text-orange-400',
    button:
      'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-orange-500/30',
  },
  indigo: {
    iconBg: 'bg-indigo-100 dark:bg-indigo-500/20',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    button:
      'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 shadow-indigo-500/30',
  },
  emerald: {
    iconBg: 'bg-emerald-100 dark:bg-emerald-500/20',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    button:
      'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-emerald-500/30',
  },
} as const;

const RatingPanel: React.FC<RatingPanelProps> = ({
  title,
  subtitle,
  onSubmit,
  onCancel,
  initialRating = 0,
  initialComment = '',
  busy = false,
  tone = 'orange',
  submitLabel,
  commentPlaceholder,
  showCancel = false,
  cancelLabel,
}) => {
  const [rating, setRating] = useState(initialRating);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState(initialComment);

  useEffect(() => setRating(initialRating), [initialRating]);
  useEffect(() => setComment(initialComment), [initialComment]);

  const styles = TONE_STYLES[tone];
  const displayRating = hovered || rating;

  const handleStar = (n: number) => {
    haptic(6);
    setRating(n);
  };

  const handleSubmit = async () => {
    if (rating < 1 || rating > 5 || busy) return;
    haptic(12);
    await onSubmit(rating, comment.trim());
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${styles.iconBg}`}
        >
          <Star className={`w-6 h-6 ${styles.iconColor} fill-current`} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-extrabold text-gray-900 dark:text-white text-[16px] leading-tight">
            {title}
          </h3>
          {subtitle && (
            <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-snug mt-0.5">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {/* Stars */}
      <div className="flex items-center justify-center gap-2 py-3">
        {[1, 2, 3, 4, 5].map((n) => {
          const filled = n <= displayRating;
          return (
            <motion.button
              key={n}
              type="button"
              whileTap={{ scale: 0.85 }}
              onClick={() => handleStar(n)}
              onMouseEnter={() => setHovered(n)}
              onMouseLeave={() => setHovered(0)}
              disabled={busy}
              className="p-1 rounded-xl transition-colors"
              aria-label={`Rate ${n}`}
            >
              <Star
                className={`w-9 h-9 transition-all duration-150 ${
                  filled
                    ? 'fill-yellow-500 text-yellow-500 drop-shadow-sm'
                    : 'text-gray-300 dark:text-gray-600'
                }`}
              />
            </motion.button>
          );
        })}
      </div>

      {/* Comment */}
      <div>
        <textarea
          rows={3}
          maxLength={500}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={commentPlaceholder || 'Rəyiniz (opsional)…'}
          disabled={busy}
          className="w-full bg-gray-50 dark:bg-white/5 border-2 border-transparent focus:border-orange-400 rounded-2xl p-3 text-[13px] outline-none font-medium resize-none transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500 text-gray-900 dark:text-white"
        />
        <div className="text-right mt-1 text-[10px] text-gray-400 pos-tabular">
          {comment.length}/500
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        {showCancel && onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="flex-1 py-3 rounded-2xl bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 text-sm font-bold active:scale-[0.98] transition-all disabled:opacity-60"
          >
            {cancelLabel || 'Ləğv et'}
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={busy || rating < 1}
          className={`flex-1 inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl text-white text-[15px] font-bold shadow-lg active:scale-[0.98] transition-all disabled:bg-gray-300 dark:disabled:bg-white/10 disabled:shadow-none disabled:cursor-not-allowed ${
            rating > 0 ? styles.button : ''
          }`}
        >
          {busy ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {submitLabel || 'Göndər'}
        </button>
      </div>
    </div>
  );
};

export default RatingPanel;

// ---------------------------------------------------------------------------
// Modal wrapper — "Restorana qiymət ver" gibi akışlar için.
// ---------------------------------------------------------------------------

export interface RatingModalProps extends RatingPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  onClose,
  ...panelProps
}) => (
  <AnimatePresence>
    {isOpen && (
      <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center sm:p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 24 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="relative w-full max-w-md bg-white dark:bg-[#0a0a0a] rounded-t-[28px] sm:rounded-[28px] shadow-2xl border border-black/5 dark:border-white/10 overflow-hidden"
        >
          {/* Drag handle (mobile) */}
          <div className="sm:hidden pt-2 pb-1 flex justify-center">
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-white/15" />
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-gray-100 dark:bg-white/10 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15 z-10"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="p-5 pt-6 sm:pt-5">
            <RatingPanel {...panelProps} />
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);

// ---------------------------------------------------------------------------
// "Rated already" küçük rozet — restorana/ofisanta puan verildikten sonra
// inline yerine bunu gösterip yeni-yıldız UI'ını kalıcı olarak gizleriz.
// ---------------------------------------------------------------------------

export interface RatedBadgeProps {
  title: string;
  rating: number;
}

export const RatedBadge: React.FC<RatedBadgeProps> = ({ title, rating }) => (
  <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-500/20">
    <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
      <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[13px] font-bold text-emerald-700 dark:text-emerald-300 leading-tight">
        {title}
      </p>
      <div className="flex items-center gap-0.5 mt-0.5">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            className={`w-3 h-3 ${
              n <= rating
                ? 'fill-yellow-500 text-yellow-500'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        ))}
      </div>
    </div>
  </div>
);
